import { throwIfFronteggFailed } from "@/lib/auth/auth.utils";
import {
  buildIdentityHeaders,
  fetchFronteggRolesCatalog,
  getIdentityBaseUrl,
  useIdentityAuth,
} from "@/lib/auth/users/users.client";
import {
  sanitizeRoleAssignmentDiff,
  type AuthActorRoleRef,
} from "@/lib/auth/users/users.roles";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";
import {
  AUTH_GROUPS_QUERY_KEY,
  diffGroupIds,
  diffUserIds,
  parseFronteggGroupsPayload,
} from "./groups.utils";

export type AuthGroupWriteInput = {
  name: string;
  color: string;
  roleIds: string[];
  userIds?: string[];
  description: string;
};

export type AuthGroupUpdateInput = AuthGroupWriteInput & {
  groupId: string;
  currentRoleIds: string[];
  currentUserIds?: string[];
};

export {
  AUTH_GROUPS_QUERY_KEY,
  diffGroupIds,
  diffUserIds,
  getCurrentRoleIdsForGroup,
  getCurrentUserIdsForGroup,
  getFronteggGroupRoleIds,
  getFronteggGroupUserIds,
  getGroupIdsForUser,
  parseFronteggGroupsPayload,
  type FronteggGroup,
  type FronteggGroupOption,
  type FronteggGroupRole,
  type FronteggGroupUser,
} from "./groups.utils";

function identityJsonHeaders(
  token: string,
  tenantId: string,
): Record<string, string> {
  return {
    ...buildIdentityHeaders(token, tenantId),
    "Content-Type": "application/json",
  };
}

async function syncGroupRoles(options: {
  token: string;
  groupId: string;
  tenantId: string;
  nextRoleIds: string[];
  actorRoles: AuthActorRoleRef[];
  currentRoleIds: string[];
}): Promise<void> {
  const {
    token,
    groupId,
    tenantId,
    nextRoleIds,
    actorRoles,
    currentRoleIds,
  } = options;

  const catalog = await fetchFronteggRolesCatalog(token, tenantId);
  const { addRoleIds, removeRoleIds } = sanitizeRoleAssignmentDiff({
    catalog,
    actorRoles,
    nextRoleIds,
    currentRoleIds,
  });
  const url = `${getIdentityBaseUrl()}/resources/groups/v1/${groupId}/roles`;

  if (addRoleIds.length > 0) {
    const response = await fetch(url, {
      body: JSON.stringify({ roleIds: addRoleIds }),
      headers: identityJsonHeaders(token, tenantId),
      method: "POST",
    });
    await throwIfFronteggFailed(response, "Frontegg add group roles failed");
  }

  if (removeRoleIds.length > 0) {
    const response = await fetch(url, {
      body: JSON.stringify({ roleIds: removeRoleIds }),
      headers: identityJsonHeaders(token, tenantId),
      method: "DELETE",
    });
    await throwIfFronteggFailed(
      response,
      "Frontegg remove group roles failed",
    );
  }
}

/**
 * Syncs membership for one group by posting added user ids and deleting removed
 * ones. Used by group create/update and by per-user membership sync.
 */
export async function syncGroupUsers(options: {
  token: string;
  groupId: string;
  tenantId: string;
  nextUserIds: string[];
  currentUserIds: string[];
}): Promise<void> {
  const { token, groupId, tenantId, nextUserIds, currentUserIds } = options;
  const { addUserIds, removeUserIds } = diffUserIds(
    currentUserIds,
    nextUserIds,
  );
  const url = `${getIdentityBaseUrl()}/resources/groups/v1/${groupId}/users`;

  if (addUserIds.length > 0) {
    const response = await fetch(url, {
      body: JSON.stringify({ userIds: addUserIds }),
      headers: identityJsonHeaders(token, tenantId),
      method: "POST",
    });
    await throwIfFronteggFailed(response, "Frontegg add group users failed");
  }

  if (removeUserIds.length > 0) {
    const response = await fetch(url, {
      body: JSON.stringify({ userIds: removeUserIds }),
      headers: identityJsonHeaders(token, tenantId),
      method: "DELETE",
    });
    await throwIfFronteggFailed(
      response,
      "Frontegg remove group users failed",
    );
  }
}

export type SyncUserGroupMembershipInput = {
  userId: string;
  currentGroupIds: string[];
  nextGroupIds: string[];
};

/**
 * Applies a user's group membership diff: for each added group, POST the user;
 * for each removed group, DELETE the user. Reuses {@link syncGroupUsers}.
 */
export async function syncUserGroupMembership(options: {
  token: string;
  userId: string;
  tenantId: string;
  nextGroupIds: string[];
  currentGroupIds: string[];
}): Promise<void> {
  const { token, userId, tenantId, nextGroupIds, currentGroupIds } = options;
  const { addGroupIds, removeGroupIds } = diffGroupIds(
    currentGroupIds,
    nextGroupIds,
  );

  for (const groupId of addGroupIds) {
    await syncGroupUsers({
      token,
      groupId,
      tenantId,
      nextUserIds: [userId],
      currentUserIds: [],
    });
  }

  for (const groupId of removeGroupIds) {
    await syncGroupUsers({
      token,
      groupId,
      tenantId,
      nextUserIds: [],
      currentUserIds: [userId],
    });
  }
}

function parseCreatedGroupId(json: unknown): string {
  if (
    json &&
    typeof json === "object" &&
    "id" in json &&
    typeof (json as { id: unknown }).id === "string"
  ) {
    return (json as { id: string }).id;
  }
  throw new Error("Frontegg create group response missing id");
}

// =============================================================================
// /identity/resources/groups/v1
// =============================================================================

export function useAuthGroups() {
  const { token, tenantId } = useIdentityAuth();

  return useQuery({
    enabled: Boolean(token && tenantId),
    queryFn: async () => {
      const response = await fetch(
        `${getIdentityBaseUrl()}/resources/groups/v1?_groupsRelations=rolesAndUsers`,
        { headers: buildIdentityHeaders(token!, tenantId) },
      );
      await throwIfFronteggFailed(response, "Frontegg groups request failed");
      const json: unknown = await response.json();
      return parseFronteggGroupsPayload(json);
    },
    queryKey: [...AUTH_GROUPS_QUERY_KEY, { tenantId }],
  });
}

export function useInvalidateAuthGroups() {
  const queryClient = useQueryClient();

  return function invalidateAuthGroups() {
    return queryClient.invalidateQueries({ queryKey: AUTH_GROUPS_QUERY_KEY });
  };
}

export function useSyncUserGroupMembership(
  options?: UseMutationOptions<void, Error, SyncUserGroupMembershipInput>,
) {
  const invalidateAuthGroups = useInvalidateAuthGroups();
  const { token, tenantId } = useIdentityAuth();

  return useMutation({
    ...options,
    mutationFn: async (input: SyncUserGroupMembershipInput) => {
      if (!token || !tenantId) {
        throw new Error("Missing auth token or tenant");
      }
      await syncUserGroupMembership({
        token,
        tenantId,
        userId: input.userId,
        nextGroupIds: input.nextGroupIds,
        currentGroupIds: input.currentGroupIds,
      });
    },
    onSuccess: async (...args) => {
      await invalidateAuthGroups();
      await options?.onSuccess?.(...args);
    },
  });
}

export function useCreateAuthGroup() {
  const invalidateAuthGroups = useInvalidateAuthGroups();
  const { token, tenantId, actorRoles } = useIdentityAuth();

  return useMutation({
    mutationFn: async (input: AuthGroupWriteInput) => {
      if (!token || !tenantId) {
        throw new Error("Missing auth token or tenant");
      }
      const response = await fetch(
        `${getIdentityBaseUrl()}/resources/groups/v1`,
        {
          body: JSON.stringify({
            name: input.name,
            color: input.color,
            description: input.description,
          }),
          headers: identityJsonHeaders(token, tenantId),
          method: "POST",
        },
      );
      await throwIfFronteggFailed(response, "Frontegg create group failed");
      const json: unknown = await response.json();
      const groupId = parseCreatedGroupId(json);
      await syncGroupRoles({
        token,
        groupId,
        tenantId,
        actorRoles,
        nextRoleIds: input.roleIds,
        currentRoleIds: [],
      });
      await syncGroupUsers({
        token,
        groupId,
        tenantId,
        nextUserIds: input.userIds ?? [],
        currentUserIds: [],
      });
      return groupId;
    },
    onSuccess: () => {
      void invalidateAuthGroups();
    },
  });
}

export function useUpdateAuthGroup() {
  const invalidateAuthGroups = useInvalidateAuthGroups();
  const { token, tenantId, actorRoles } = useIdentityAuth();

  return useMutation({
    mutationFn: async (input: AuthGroupUpdateInput) => {
      if (!token || !tenantId) {
        throw new Error("Missing auth token or tenant");
      }
      const response = await fetch(
        `${getIdentityBaseUrl()}/resources/groups/v1/${input.groupId}`,
        {
          body: JSON.stringify({
            name: input.name,
            color: input.color,
            description: input.description,
          }),
          headers: identityJsonHeaders(token, tenantId),
          method: "PATCH",
        },
      );
      await throwIfFronteggFailed(response, "Frontegg update group failed");
      await syncGroupRoles({
        token,
        tenantId,
        actorRoles,
        groupId: input.groupId,
        nextRoleIds: input.roleIds,
        currentRoleIds: input.currentRoleIds,
      });
      await syncGroupUsers({
        token,
        tenantId,
        groupId: input.groupId,
        nextUserIds: input.userIds ?? [],
        currentUserIds: input.currentUserIds ?? [],
      });
    },
    onSuccess: () => {
      void invalidateAuthGroups();
    },
  });
}

export function useDeleteAuthGroup() {
  const invalidateAuthGroups = useInvalidateAuthGroups();
  const { token, tenantId } = useIdentityAuth();

  return useMutation({
    mutationFn: async (groupId: string) => {
      if (!token || !tenantId) {
        throw new Error("Missing auth token or tenant");
      }
      const response = await fetch(
        `${getIdentityBaseUrl()}/resources/groups/v1/${groupId}`,
        {
          headers: buildIdentityHeaders(token, tenantId),
          method: "DELETE",
        },
      );
      await throwIfFronteggFailed(response, "Frontegg delete group failed");
    },
    onSuccess: () => {
      void invalidateAuthGroups();
    },
  });
}
